import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { useCurrentUser } from "@/shared/auth";

//import images — fallback when the user has no uploaded photo
import avatar1 from "../../assets/images/users/avatar-1.jpg";

/**
 * ProfileDropdown — pure-Tailwind dropdown (Bootstrap classes removed 2026-06-22).
 * Hand-rolled open/close (click-outside + Escape). Reproduces the prior Velzon
 * look (avatar + name on xl+, gradient menu header, item rows) with `tw-` only,
 * so it survives the Bootstrap-CSS removal.
 */
const ProfileDropdown = () => {
  const user = useSelector((state: any) => state.Profile.user);
  // Avatar lives on the logged-in user (state.Login.user) — kept in sync by
  // the profile-photo upload/remove flow via the `setUserAvatar` action.
  const authUser = useCurrentUser();
  const avatarSrc = authUser?.avatarUrl || avatar1;

  const [userName, setUserName] = useState("Admin");

  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      setUserName(user.firstName || user.userName || user.email || "Admin");
    } else {
      try {
        const persistedState = sessionStorage.getItem("persist:root");
        if (persistedState) {
          const parsed = JSON.parse(persistedState);
          if (parsed.Login) {
            const loginState = JSON.parse(parsed.Login);
            if (loginState.user && Object.keys(loginState.user).length > 0) {
              const authUser = loginState.user;
              setUserName(
                authUser.firstName ||
                  authUser.userName ||
                  authUser.email ||
                  "Admin"
              );
            }
          }
        }
      } catch (e) {
        console.error("Error reading user from Redux Persist:", e);
      }
    }
  }, [user]);

  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [isOpen]);

  const itemClass =
    "tw-flex tw-items-center tw-gap-2 tw-w-full tw-rounded-md tw-my-0.5 tw-px-3 tw-py-2 tw-text-sm tw-text-slate-700 tw-no-underline hover:tw-bg-slate-100 hover:tw-text-slate-700";

  return (
    <div ref={wrapperRef} className="tw-relative tw-flex tw-items-center sm:tw-ml-3">
      <button
        type="button"
        className="tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-border-0 tw-bg-transparent tw-p-1.5 tw-cursor-pointer hover:tw-bg-slate-100"
        onClick={() => setIsOpen((o) => !o)}
        aria-expanded={isOpen}
      >
        <span className="tw-relative tw-inline-flex">
          <img
            className="tw-rounded-full"
            src={avatarSrc}
            alt="Header Avatar"
            onError={(e) => {
              if (e.currentTarget.src !== avatar1) e.currentTarget.src = avatar1;
            }}
            style={{
              width: "36px",
              height: "36px",
              objectFit: "cover",
              border: "2px solid #e2e8f0",
            }}
          />
          <span
            className="tw-absolute tw-bottom-0 tw-right-0 tw-rounded-full tw-bg-emerald-500"
            style={{ width: "10px", height: "10px", border: "2px solid white" }}
          ></span>
        </span>
        <span className="tw-hidden tw-text-left xl:tw-block">
          <span
            className="tw-block tw-font-semibold tw-text-slate-700"
            style={{ fontSize: "0.9375rem", lineHeight: "1.2" }}
          >
            {userName}
          </span>
          <span
            className="tw-block tw-text-slate-500"
            style={{ fontSize: "0.75rem" }}
          >
            Online
          </span>
        </span>
        <i
          className="ri-arrow-down-s-line tw-hidden xl:tw-block"
          style={{ fontSize: "0.875rem", color: "#718096" }}
        ></i>
      </button>

      {isOpen && (
        <div
          className="tw-absolute tw-right-0 tw-top-full tw-z-[10000] tw-mt-1.5 tw-max-h-[calc(100vh-100px)] tw-min-w-[180px] tw-overflow-y-auto tw-rounded-lg tw-border-0 tw-bg-white tw-p-1.5"
          style={{ boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)" }}
        >
          <div
            className="tw-mb-1.5 tw-rounded-md tw-p-2.5 tw-text-white"
            style={{
              background: "linear-gradient(135deg, #1A73E8 0%, #0D47A1 100%)",
            }}
          >
            <div className="tw-flex tw-items-center tw-gap-2">
              <img
                className="tw-rounded-full"
                src={avatarSrc}
                alt="Avatar"
                onError={(e) => {
                  if (e.currentTarget.src !== avatar1)
                    e.currentTarget.src = avatar1;
                }}
                style={{
                  width: "32px",
                  height: "32px",
                  objectFit: "cover",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                }}
              />
              <div>
                <div className="tw-font-semibold" style={{ fontSize: "0.875rem" }}>
                  {userName}
                </div>
                <div style={{ fontSize: "0.7rem", opacity: 0.9 }}>
                  Welcome back!
                </div>
              </div>
            </div>
          </div>

          <a
            href={(process.env.PUBLIC_URL || "") + "/profile"}
            className={itemClass}
            onClick={() => setIsOpen(false)}
          >
            <i
              className="ri-user-line"
              style={{ fontSize: "1rem", color: "#718096", width: "18px" }}
            ></i>
            <span>My Profile</span>
          </a>
          <div className="tw-my-1.5 tw-border-t tw-border-slate-200"></div>
          <a
            href={(process.env.PUBLIC_URL || "") + "/logout"}
            className={
              "tw-flex tw-items-center tw-gap-2 tw-w-full tw-rounded-md tw-my-0.5 tw-px-3 tw-py-2 tw-text-sm tw-no-underline hover:tw-bg-rose-50"
            }
            style={{ color: "#e53e3e" }}
            onClick={() => setIsOpen(false)}
          >
            <i
              className="ri-logout-box-line"
              style={{ fontSize: "1rem", width: "18px" }}
            ></i>
            <span>Logout</span>
          </a>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
